import React from "react";
import Button from "./Button";
import colors from "../theme/colors";
import typography from "../theme/typography";

export default function EmptyState({
  title,
  description,
  icon = null,
  actionLabel,
  onAction,
  align = "center",
  maxWidth,
  size = "md",
  variant = "default",
  disabled = false,
  style = {},
  className = "",
  children,
}) {
  const sizeMap = {
    sm: {
      containerGap: 8,
      iconSize: 18,
      titleSize: typography.size.xs,
      descriptionSize: typography.size.xs,
      buttonSize: "sm",
      defaultMaxWidth: 240,
      actionMarginTop: 4,
      contentGap: 4,
    },
    md: {
      containerGap: 10,
      iconSize: 20,
      titleSize: typography.size.sm,
      descriptionSize: typography.size.xs,
      buttonSize: "sm",
      defaultMaxWidth: 280,
      actionMarginTop: 6,
      contentGap: 6,
    },
    lg: {
      containerGap: 12,
      iconSize: 24,
      titleSize: typography.size.base,
      descriptionSize: typography.size.sm,
      buttonSize: "md",
      defaultMaxWidth: 340,
      actionMarginTop: 8,
      contentGap: 8,
    },
  };

  const variantMap = {
    default: {
      iconColor: colors.textMuted,
      titleColor: colors.textPrimary,
      descriptionColor: colors.textSecondary,
    },
    soft: {
      iconColor: colors.primary,
      titleColor: colors.primaryDark,
      descriptionColor: colors.textSecondary,
    },
  };

  const resolvedSize = sizeMap[size] || sizeMap.md;
  const resolvedVariant = variantMap[variant] || variantMap.default;
  const resolvedMaxWidth = maxWidth || resolvedSize.defaultMaxWidth;
  const hasAction = Boolean(actionLabel && onAction);

  const justifyContent =
    align === "center"
      ? "center"
      : align === "left"
      ? "flex-start"
      : "flex-end";

  const alignItems =
    align === "center"
      ? "center"
      : align === "left"
      ? "flex-start"
      : "flex-end";

  const textAlign =
    align === "left" ? "left" : align === "right" ? "right" : "center";

  return (
    <div
      className={className}
      style={{
        width: "100%",
        display: "flex",
        justifyContent,
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems,
          textAlign,
          maxWidth: resolvedMaxWidth,
          gap: resolvedSize.containerGap,
          width: "100%",
        }}
      >
        {icon ? (
          <div
            style={{
              color: resolvedVariant.iconColor,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 0,
              fontSize: resolvedSize.iconSize,
            }}
          >
            {icon}
          </div>
        ) : null}

        {(title || description || children) ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems,
              gap: resolvedSize.contentGap,
              width: "100%",
            }}
          >
            {title ? (
              <div
                style={{
                  fontFamily: typography.fontFamily,
                  fontSize: resolvedSize.titleSize,
                  fontWeight: typography.weight.bold,
                  lineHeight: typography.lineHeight.sm,
                  color: resolvedVariant.titleColor,
                }}
              >
                {title}
              </div>
            ) : null}

            {description ? (
              <div
                style={{
                  fontFamily: typography.fontFamily,
                  fontSize: resolvedSize.descriptionSize,
                  fontWeight: typography.weight.regular,
                  lineHeight: typography.lineHeight.base,
                  color: resolvedVariant.descriptionColor,
                }}
              >
                {description}
              </div>
            ) : null}

            {children ? <div>{children}</div> : null}
          </div>
        ) : null}

        {hasAction ? (
          <div style={{ marginTop: resolvedSize.actionMarginTop }}>
            <Button
              type="button"
              onClick={onAction}
              variant="primary"
              size={resolvedSize.buttonSize}
              disabled={disabled}
            >
              {actionLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}